import type {
  Unit,
  PlanConfig,
  PretestQuestion,
  LearningStyleQuestion,
  LearningStyleProfile,
  AssessmentQuestionType,
} from '@/types'

// ========================
// 课程数据 - 湘少版教材
// ========================

export const units: Unit[] = [
  {
    id: 'G4-上-U1',
    grade: 'G4',
    semester: '上',
    unitNum: 1,
    title: 'This is our school',
    titleCn: '这是我们的学校',
    theme: '学校场所与活动',
    vocab: [
      { word: 'classroom', meaning: '教室' },
      { word: 'playground', meaning: '操场' },
      { word: 'library', meaning: '图书馆' },
      { word: 'music room', meaning: '音乐教室' },
      { word: 'art room', meaning: '美术教室' },
      { word: 'computer', meaning: '电脑；计算机' },
      { word: 'office', meaning: '办公室' },
      { word: 'story', meaning: '故事' },
      { word: 'a lot of', meaning: '许多' },
      { word: 'and', meaning: '和；又；与' },
      { word: 'canteen', meaning: '餐厅；食堂' },
      { word: 'class', meaning: '（一节）课；上课时间' },
      { word: 'colour', meaning: '给……上色' },
      { word: 'desk', meaning: '书桌；办公桌' },
      { word: 'eat', meaning: '吃' },
      { word: 'fun', meaning: '乐趣' },
      { word: 'garden', meaning: '花园' },
      { word: 'grade', meaning: '年级' },
      { word: 'lion', meaning: '狮子' },
      { word: 'lunch', meaning: '午餐' },
      { word: 'lunchtime', meaning: '午餐时间' },
      { word: 'of', meaning: '……的' },
      { word: 'on', meaning: '在……上面' },
      { word: 'read', meaning: '阅读； 读' },
      { word: 'together', meaning: '一起' },
    ],
    sentences: [
      {
        pattern: 'This is (the)... That is (the) ...',
        examples: [
          'That is the music room.',
          'This is the teachers\' office.',
          'That is the classroom.',
          'This is our shcool.',
          'That is the art room.)',
        ],
      },
    ],
    text: {
      partA: 'A：Welcome to school!\n     This is our classroom.\n     That is the playground.\n     This is the library.\nB：Shh…\nA：It’s lunchtime. Let’s go and eat.',
    },
    phonics: {
      letter: 'Ll',
      words: ['lion', 'letter', 'line', 'light'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '表 1 单词 + 句型 1',
        moduleOrder: ['vocab', 'sentence'],
        sections: {
          vocabLearning: [
            { word: 'classroom', meaning: '教室' },
            { word: 'library', meaning: '图书馆' },
            { word: 'playground', meaning: '操场' },
            { word: 'music room', meaning: '音乐教室' },
            { word: 'art room', meaning: '美术室' },
            { word: 'computer', meaning: '电脑' },
            { word: 'office', meaning: '办公室' },
            { word: 'story', meaning: '故事' },
          ],
          sentenceLearning: [
            {
              pattern: 'This is our …',
              examples: ['This is our classroom.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '表 2 单词 + 句型 2',
        moduleOrder: ['vocab', 'sentence'],
        sections: {
          vocabLearning: [
            { word: 'a lot of', meaning: '许多' },
            { word: 'and', meaning: '和；又；与' },
            { word: 'canteen', meaning: '餐厅；食堂' },
            { word: 'colour', meaning: '给……上色' },
            { word: 'desk', meaning: '书桌；办公桌' },
            { word: 'eat', meaning: '吃' },
            { word: 'fun', meaning: '乐趣' },
          ],
          sentenceLearning: [
            {
              pattern: 'This is (the)... That is (the) ...',
              examples: ['(This is the playground.', 'That is the music room.', 'This is the teachers\' office.', 'That is the classroom.', 'This is our shcool.', 'That is the art room.)'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '表 3 单词 + 句型 3',
        moduleOrder: ['vocab', 'sentence'],
        sections: {
          vocabLearning: [
            { word: 'garden', meaning: '花园' },
            { word: 'grade', meaning: '年级' },
            { word: 'lion', meaning: '狮子' },
            { word: 'lunch', meaning: '午餐' },
            { word: 'lunchtime', meaning: '午餐时间' },
            { word: 'on', meaning: '在……上面' },
            { word: 'read', meaning: '阅读； 读' },
            { word: 'together', meaning: '一起' },
          ],
          sentenceLearning: [
            {
              pattern: 'There is ... There are ...',
              examples: ['There is a library.There are many books.', 'There is a classroom. There are many desks.', 'There is a playground. There are many students.'],
            },
          ],
        },
      },
      {
        lessonNum: 4,
        title: '课文对话 + 自然拼读',
        moduleOrder: ['text', 'phonics'],
        sections: {
          textLearning: 'Part A 课文对话 → 配音与背诵',
          phonicsLearning: '字母 Ll 发音：lion, letter, line, light',
        },
      },
    ],
  },
  {
    id: 'G4-上-U2',
    grade: 'G4',
    semester: '上',
    unitNum: 2,
    title: 'What time do you get up?',
    titleCn: '你几点起床？',
    theme: '时间表达与日常作息',
    vocab: [
      { word: 'up', meaning: '向上地' },
      { word: 'get up', meaning: '起床' },
      { word: 'at', meaning: '在；向' },
      { word: 'early', meaning: '早的；早地' },
      { word: 'breakfast', meaning: '早餐' },
      { word: 'hurry', meaning: '赶紧' },
      { word: 'half', meaning: '一半；半' },
      { word: 'past', meaning: '在……之后' },
      { word: 'lunch', meaning: '午餐' },
      { word: 'dinner', meaning: '晚餐；正餐' },
      { word: 'go to bed', meaning: '上床睡觉' },
      { word: 'evening', meaning: '晚上；傍晚' },
      { word: 'after', meaning: '在……之后' },
      { word: 'alarm clock', meaning: '闹钟' },
      { word: 'basketball', meaning: '篮球' },
      { word: 'guess', meaning: '猜' },
      { word: 'home', meaning: '家；住所；窝' },
      { word: 'Hurry up!', meaning: '快点！' },
      { word: 'late', meaning: '迟的' },
      { word: 'Monday', meaning: '星期一' },
      { word: 'monkey', meaning: '猴子' },
      { word: 'never', meaning: '从不' },
      { word: 'September', meaning: '九月' },
      { word: 'still', meaning: '还；仍然' },
      { word: 'where', meaning: '在哪里' },
    ],
    sentences: [
      {
        pattern: '— What time do you get up / have breakfast / go to school?',
        examples: [],
      },
      {
        pattern: '— I get up at 7:00. / I have breakfast at half past seven.',
        examples: [],
      },
      {
        pattern: 'It’s time to get up / go to school / go to bed.',
        examples: [],
      },
      {
        pattern: 'It’s time for breakfast / lunch / dinner.',
        examples: [],
      },
    ],
    text: {
      partA: 'A: What time do you get up?\nB: Hmm… I get up at 7:00. \nA: Look at the alarm clock.\nB:It’s still early.\nMum: It’s 7:30 now. It’s time to have breakfast.\nA:Hurry up!\nA&amp;B: Bye, Mum.\nMum: Bye.',
    },
    phonics: {
      letter: 'Mm',
      words: ['milk', 'monkey', 'mango', 'moon'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'up', meaning: '向上地' },
            { word: 'get up', meaning: '起床' },
            { word: 'at', meaning: '在；向' },
            { word: 'early', meaning: '早的；早地' },
            { word: 'breakfast', meaning: '早餐' },
            { word: 'hurry', meaning: '赶紧' },
            { word: 'half', meaning: '一半；半' },
            { word: 'past', meaning: '在……之后' },
            { word: 'lunch', meaning: '午餐' },
            { word: 'dinner', meaning: '晚餐；正餐' },
            { word: 'go to bed', meaning: '上床睡觉' },
            { word: 'evening', meaning: '晚上；傍晚' },
          ],
          sentenceLearning: [
            {
              pattern: 'What time do you ...',
              examples: ['I ...at ...', '— What time do you get up?', '— I get up at 7:oo.', '— What time do you have breakfast', '— I have breakfast at half past seven.', '— What time do you go to school?', '— I go to school at 8:00.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'after', meaning: '在……之后' },
            { word: 'alarm clock', meaning: '闹钟' },
            { word: 'basketball', meaning: '篮球' },
            { word: 'first(1st)', meaning: '第一；第一的' },
            { word: 'guess', meaning: '猜' },
            { word: 'home', meaning: '家；住所；窝' },
            { word: 'Hurry up!', meaning: '快点！' },
          ],
          sentenceLearning: [
            {
              pattern: 'It‘s time to ...',
              examples: ['It’s time to get up / go to school / go to bed.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'late', meaning: '迟的' },
            { word: 'Monday', meaning: '星期一' },
            { word: 'monkey', meaning: '猴子' },
            { word: 'never', meaning: '从不' },
            { word: 'September', meaning: '九月' },
            { word: 'still', meaning: '还；仍然' },
            { word: 'where', meaning: '在哪里' },
          ],
          sentenceLearning: [
            {
              pattern: 'It\'s time for ...',
              examples: ['It’s time for breakfast / lunch / dinner.'],
            },
          ],
          phonicsLearning: '字母 Mm 发音：milk, monkey, mango, moon',
        },
      },
    ],
  },
  {
    id: 'G4-上-U3',
    grade: 'G4',
    semester: '上',
    unitNum: 3,
    title: 'What do you like?',
    titleCn: '你喜欢什么？',
    theme: '食物与喜好',
    vocab: [
      { word: 'dumpling', meaning: '水饺；汤圆' },
      { word: 'rice', meaning: '米；米饭' },
      { word: 'fish', meaning: '鱼肉；鱼；' },
      { word: 'and', meaning: '和；又；与' },
      { word: 'vegetable', meaning: '蔬菜' },
      { word: 'bread', meaning: '面包' },
      { word: 'chicken', meaning: '鸡肉；鸡' },
      { word: 'hamburger', meaning: '汉堡包' },
      { word: 'has', meaning: 'have的第三人称单数形式' },
      { word: 'say', meaning: '说' },
      { word: 'they', meaning: '他（她；它）们' },
      { word: 'yummy', meaning: '美味的' },
    ],
    sentences: [
      {
        pattern: '— What do you like?',
        examples: [],
      },
      {
        pattern: '— I like dumplings / rice / fish / vegetables/noodles/chicken.',
        examples: [],
      },
      {
        pattern: '— Do you like hamburgers/bread?',
        examples: [],
      },
      {
        pattern: '— Yes, I do. / No, I don’t.',
        examples: [],
      },
      {
        pattern: 'I like chicken and potatoes/ I like bread and vegetables/ I like dumplings and noodles.',
        examples: [],
      },
    ],
    text: {
      partA: 'A：I’m hungry.\nB：Me too.\nA: I like dumplings.\nC: I like rice, fish and vegetables.\n    What do you like,Peter?\nB: I like bread, chicken and vegetables.\n    I like hamburgers.',
    },
    phonics: {
      letter: 'Nn',
      words: ['nose', 'night', 'nine', 'net'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'dumpling', meaning: '水饺；汤圆' },
            { word: 'rice', meaning: '米；米饭' },
            { word: 'fish', meaning: '鱼肉；鱼；' },
            { word: 'and', meaning: '和；又；与' },
            { word: 'vegetable', meaning: '蔬菜' },
          ],
          sentenceLearning: [
            {
              pattern: 'What do you like?',
              examples: ['I like ...', '— What do you like?', '— I like dumplings / rice / fish / vegetables/noodles/chicken.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'bread', meaning: '面包' },
            { word: 'chicken', meaning: '鸡肉；鸡' },
            { word: 'hamburger', meaning: '汉堡包' },
          ],
          sentenceLearning: [
            {
              pattern: 'Do you like ...?',
              examples: ['Yes, I do. / No, I don’t.', '— Do you like hamburgers/bread/noodles?', '— Yes, I do. / No, I don’t.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'say', meaning: '说' },
            { word: 'they', meaning: '他（她；它）们' },
            { word: 'yummy', meaning: '美味的' },
          ],
          sentenceLearning: [
            {
              pattern: 'I like ... and ...',
              examples: ['I like hamburgers and bread.', 'I like rice, noodles and dumplings.', 'I like fish, vegetable and rice.'],
            },
          ],
          phonicsLearning: '字母 Nn 发音：nose, night, nine, net',
        },
      },
    ],
  },
  {
    id: 'G4-上-U4',
    grade: 'G4',
    semester: '上',
    unitNum: 4,
    title: 'Look at the elephant',
    titleCn: '看这只大象',
    theme: '动物描述',
    vocab: [
      { word: 'elephant', meaning: '大象' },
      { word: 'tall', meaning: '高的' },
      { word: 'lion', meaning: '狮子' },
      { word: 'strong', meaning: '健壮的' },
      { word: 'tiger', meaning: '老虎' },
      { word: 'panda', meaning: '大熊猫' },
      { word: 'cute', meaning: '漂亮的；逗人喜爱的' },
      { word: 'monkey', meaning: '猴子' },
      { word: 'funny', meaning: '使人发笑的；' },
      { word: 'beautiful', meaning: '美丽的；' },
      { word: 'bear', meaning: '熊' },
      { word: 'friend', meaning: '朋友' },
      { word: 'cry', meaning: '哭；哭泣' },
      { word: 'animal', meaning: '动物' },
      { word: 'Australia', meaning: '澳大利亚' },
      { word: 'China', meaning: '中国' },
      { word: 'each other', meaning: '相互' },
      { word: 'kangaroo', meaning: '袋鼠' },
      { word: 'live', meaning: '住；居住' },
      { word: 'photo', meaning: '照片' },
      { word: 'right', meaning: '正确的' },
      { word: 'tree', meaning: '树' },
    ],
    sentences: [
      {
        pattern: '— Look at the elephant / panda / monkey.',
        examples: [],
      },
      {
        pattern: '— It’s big / small / strong / cute / funny / beautiful.',
        examples: [],
      },
      {
        pattern: '— Is it a lion?',
        examples: [],
      },
      {
        pattern: '— No, it isn’t. It’s a tiger.',
        examples: [],
      },
      {
        pattern: '— What\'s that?',
        examples: [],
      },
      {
        pattern: '— It\'s a mongkey.',
        examples: [],
      },
      {
        pattern: 'It\'s a panda.',
        examples: [],
      },
      {
        pattern: 'It\'s a tiger.',
        examples: [],
      },
      {
        pattern: 'It\'s a bird.',
        examples: [],
      },
    ],
    text: {
      partA: 'Guide: Welcome!\nA: Look at the elephant.\nB: It\'s big and tall.\n    What’s that?\nC: Is it a lion?\nA: No, it isn’t. It’s a strong tiger.\nB: Look at the panda. It’s cute.\nA: I like pandas.\nC: I like monkeys. They’re funny.',
    },
    phonics: {
      letter: 'Oo',
      words: ['cold', 'rose', 'photo', 'phone'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'elephant', meaning: '大象' },
            { word: 'tall', meaning: '高的' },
            { word: 'lion', meaning: '狮子' },
            { word: 'strong', meaning: '健壮的' },
            { word: 'tiger', meaning: '老虎' },
            { word: 'panda', meaning: '大熊猫' },
          ],
          sentenceLearning: [
            {
              pattern: 'Look at the ... It\'s ...',
              examples: ['— Look at the elephant.', '— It’s big.', '— Look at the panda.', '— It’s cute.', '—Look at the monkey.', '—funnyl.', '—Look at the lion.', '— It’s strong.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'cute', meaning: '漂亮的；逗人喜爱的' },
            { word: 'monkey', meaning: '猴子' },
            { word: 'funny', meaning: '使人发笑的；' },
            { word: 'beautiful', meaning: '美丽的；' },
            { word: 'bear', meaning: '熊' },
            { word: 'friend', meaning: '朋友' },
            { word: 'cry', meaning: '哭；哭泣' },
          ],
          sentenceLearning: [
            {
              pattern: 'Is it a(an) ...? Yes,it is. / No, it isn\'t. It\'s a(an) ...',
              examples: ['— Is it a dog?', '— Yes, it is.', '— Is it an elephant?', '— Yes, it is.', '— Is it a lion?', '— No, it isn’t. It’s a tiger.', '— Is it a monkey?', '— No, it isn’t. It’s a cat.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'animal', meaning: '动物' },
            { word: 'Australia', meaning: '澳大利亚' },
            { word: 'China', meaning: '中国' },
            { word: 'each other', meaning: '相互' },
            { word: 'kangaroo', meaning: '袋鼠' },
            { word: 'live', meaning: '居住' },
            { word: 'photo', meaning: '照片' },
            { word: 'right', meaning: '正确的' },
            { word: 'tree', meaning: '树' },
          ],
          sentenceLearning: [
            {
              pattern: 'What\'s that/this?  It\'s a（an) ...',
              examples: ['— What\'s this?', '— It\'s a tiger', '— What\'s this?', '— It\'s a bird.', '— What\'s that?', '— It\'s a monkey.', '— What\'s that?', '— It\'s a panda.'],
            },
          ],
          phonicsLearning: '字母 Oo 发音：cold, rose, photo, phone',
        },
      },
    ],
  },
  {
    id: 'G4-上-U5',
    grade: 'G4',
    semester: '上',
    unitNum: 5,
    title: 'Can you play basketball?',
    titleCn: '你会打篮球吗？',
    theme: '运动与能力',
    vocab: [
      { word: 'can', meaning: '能；会' },
      { word: 'basketball', meaning: '篮球' },
      { word: 'jump', meaning: '跳；跃' },
      { word: 'high', meaning: '高高地；高的' },
      { word: 'well', meaning: '熟练地；令人满意地' },
      { word: 'swim', meaning: '游泳' },
      { word: 'run', meaning: '跑；奔跑' },
      { word: 'football', meaning: '足球' },
      { word: 'fast', meaning: '快地；快的' },
      { word: 'want', meaning: '想要' },
      { word: 'far', meaning: '远地；远的' },
      { word: 'fly', meaning: '飞；飞翔' },
      { word: 'have a try', meaning: '试一试' },
      { word: 'join', meaning: '加入' },
      { word: 'rabbit', meaning: '兔子' },
      { word: 'shot', meaning: '击球；射球' },
      { word: 'talk', meaning: '交谈；说话' },
      { word: 'with', meaning: '和……一起；具有；用' },
      { word: 'write', meaning: '写；写作' },
    ],
    sentences: [
      {
        pattern: '— Can you play basketball / football / ping-pong?',
        examples: [],
      },
      {
        pattern: '— Yes, I can. / No, I can’t.',
        examples: [],
      },
      {
        pattern: '— Can you run fast / jump far / fly high?',
        examples: [],
      },
      {
        pattern: '— Yes, I can. / No, I can’t.',
        examples: [],
      },
      {
        pattern: '— I can play basketball. I can\'t swim.',
        examples: [],
      },
      {
        pattern: 'I can sing. I can\'t dance.',
        examples: [],
      },
      {
        pattern: 'I can play ping-pong. I can\'t play yo-yo.',
        examples: [],
      },
    ],
    text: {
      partA: 'A: Look! Benny can jump high.\nB: Yes. He can play basketball well.\nA: Can you play basketball, Mingming?\nB: Yes, I can. \nC:Come and join us.\nA:I can’t play basketball.\nB: Have a try.\nA: OK.',
    },
    phonics: {
      letter: 'Oo',
      words: ['dolly', 'hot', 'socks', 'pot'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'can', meaning: '能；会' },
            { word: 'basketball', meaning: '篮球' },
            { word: 'jump', meaning: '跳；跃' },
            { word: 'high', meaning: '高高地；高的' },
            { word: 'well', meaning: '熟练地；令人满意地' },
          ],
          sentenceLearning: [
            {
              pattern: 'Can you play ...?',
              examples: ['Yes, I can. / No, I can’t.', '— Can you play basketball?', '— Yes, I can.', '— Can you play football?', '— Yes, I can.', '— Can you swim?', '— No, I can’t.', '— Can you play ping-pong?', '— No, I can’t.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'swim', meaning: '游泳' },
            { word: 'run', meaning: '跑；奔跑' },
            { word: 'football', meaning: '足球' },
            { word: 'fast', meaning: '快地；快的' },
            { word: 'want', meaning: '想要' },
          ],
          sentenceLearning: [
            {
              pattern: 'Can you ...?',
              examples: ['Yes, I can. / No, I can’t.', '— Can you run fast?', '— Yes, I can.', '— Can you  jump far?', '— No, I can’t.', '— Can you fly high?', '— No, I can’t.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'far', meaning: '远地；远的' },
            { word: 'fly', meaning: '飞；飞翔' },
            { word: 'have a try', meaning: '试一试' },
            { word: 'join', meaning: '加入' },
            { word: 'rabbit', meaning: '兔子' },
            { word: 'shot', meaning: '击球；射球' },
            { word: 'talk', meaning: '交谈；说话' },
            { word: 'with', meaning: '和……一起；具有；用' },
            { word: 'write', meaning: '写；写作' },
          ],
          sentenceLearning: [
            {
              pattern: 'I can ... I can\'t ...',
              examples: ['I can play basketball. I can\'t swim.', 'I can sing. I can\'t dance.', 'I can play ping-pong. I can\'t play yo-yo.'],
            },
          ],
          phonicsLearning: '字母 Oo 发音：dolly, hot, socks, pot',
        },
      },
    ],
  },
  {
    id: 'G4-上-U6',
    grade: 'G4',
    semester: '上',
    unitNum: 6,
    title: 'What are those?',
    titleCn: '那些是什么？',
    theme: '自然事物',
    vocab: [
      { word: 'those', meaning: '那些' },
      { word: 'these', meaning: '这些' },
      { word: 'flower', meaning: '花' },
      { word: 'they', meaning: '他（她；它）们' },
      { word: 'tree', meaning: '树' },
      { word: 'bee', meaning: '蜜蜂' },
      { word: 'garden', meaning: '花园' },
      { word: 'hill', meaning: '小山' },
      { word: 'best', meaning: '最好的' },
      { word: 'have a look', meaning: '看一看' },
      { word: 'into', meaning: '到……里面；朝；向' },
      { word: 'maybe', meaning: '也许' },
      { word: 'outing', meaning: '短途旅游' },
      { word: 'river', meaning: '河流； 江' },
      { word: 'seed', meaning: '种子' },
      { word: 'wind', meaning: '风' },
    ],
    sentences: [
      {
        pattern: 'This/That is a(an)...',
        examples: [],
      },
    ],
    text: {
      partA: 'A: Look at these flowers. \nB: They’re beautiful.\nBirds: Tweet, tweet!\nA: What are those?\nB: They’re birds. They live in the trees.\nA: Look! Those are kites. \nB: They look like birds.',
    },
    phonics: {
      letter: 'Pp',
      words: ['panda', 'paper', 'pear', 'potato'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'those', meaning: '那些' },
            { word: 'these', meaning: '这些' },
            { word: 'flower', meaning: '花' },
            { word: 'they', meaning: '他（她；它）们' },
            { word: 'tree', meaning: '树' },
          ],
          sentenceLearning: [
            {
              pattern: 'This/That is a(an)...',
              examples: ['This is a bee. That is a sheep.', 'This is a tree. That is a flower.', 'This is a fish. That is a bird.', 'This is a lion. That is a tiger.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'bee', meaning: '蜜蜂' },
            { word: 'garden', meaning: '花园' },
            { word: 'hill', meaning: '小山' },
            { word: 'best', meaning: '最好的' },
          ],
          sentenceLearning: [
            {
              pattern: 'These/ Those are ...',
              examples: ['These are bees. Those are sheep.', 'These are trees. Those are flowers.', 'These are fish. Those are birds.', 'These are lions. Those are tigers.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'have a look', meaning: '看一看' },
            { word: 'into', meaning: '到……里面；朝；向' },
            { word: 'maybe', meaning: '也许' },
            { word: 'outing', meaning: '短途旅游' },
            { word: 'river', meaning: '河流； 江' },
            { word: 'seed', meaning: '种子' },
            { word: 'wind', meaning: '风' },
          ],
          sentenceLearning: [
            {
              pattern: 'What’s this/that?',
              examples: ['It\'s a(an)...', 'What are these/those?', 'They\'re ...', 'What\'s this?', 'It\'s a bee.', 'What are these?', 'They\'re bees.', 'What\'s that?', 'It\'s an eggs.', 'What\'s are those?', 'They\'re eggs.'],
            },
          ],
          phonicsLearning: '字母 Pp 发音：panda  paper pear  potato',
        },
      },
    ],
  },
  {
    id: 'G4-上-U7',
    grade: 'G4',
    semester: '上',
    unitNum: 7,
    title: 'Whose watch is this?',
    titleCn: '这是谁的手表？',
    theme: '物品归属',
    vocab: [
      { word: 'whose', meaning: '谁的' },
      { word: 'know', meaning: '知道' },
      { word: 'bag', meaning: '包' },
      { word: 'key', meaning: '钥匙' },
      { word: 'umbrella', meaning: '雨伞' },
      { word: 'their', meaning: '他（她；它）们的' },
      { word: 'wear', meaning: '穿；戴' },
      { word: 'be', meaning: '成为；是' },
      { word: 'pet', meaning: '宠物' },
      { word: 'quiet', meaning: '安静的' },
      { word: 'strange', meaning: '奇怪的' },
      { word: 'watch', meaning: '手表' },
      { word: 'zip', meaning: '拉上……拉链' },
    ],
    sentences: [
      {
        pattern: 'Whose ... are they?',
        examples: [],
      },
    ],
    text: {
      partA: 'A: Whose watch is this?\nB: I don’t know.\n     That\'s Jerry‘s.\nA: Hi, Jerry. Is this your watch?\nC: Yes, it is. Thank you.\nB: Zip your bag, please.\nC: OK.Goodbye.\nA&amp;B: Bye.',
    },
    phonics: {
      letter: 'Qq',
      words: ['queen', 'quiet', 'quilt', 'quarter'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'key', meaning: '钥匙' },
            { word: 'watch', meaning: '手表' },
            { word: 'umbrella', meaning: '伞' },
            { word: 'bag', meaning: '包' },
          ],
          sentenceLearning: [
            {
              pattern: 'Whose ... is this?',
              examples: ['It\'s ...', 'Whose ... are these?', 'They\'re ...', '-Whose watch is this?', '-It\'s Tom’s watch.', '-Whose umbrella is this?', '-It\'s Benny\'s umbrella.', '-Whose bags are these?', '-They\'re their\' bags.', '-Whose keys are these?', '-They\'re their keys.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'whose', meaning: '谁的' },
            { word: 'know', meaning: '知道' },
            { word: 'their', meaning: '他（她；它）们的' },
            { word: 'wear', meaning: '穿；戴' },
          ],
          sentenceLearning: [
            {
              pattern: 'Is this/that your ...?',
              examples: ['Yes, it is./No, it isn\'t.', '-Is this your watch?', '-Yes, it is.', '-Is this your football?', '-Yes, it is.', '-Is that your umbrella?', '-No, it isn\'t.', '-Is that your book?', '-No, it isn\'t.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'be', meaning: '成为；是' },
            { word: 'pet', meaning: '宠物' },
            { word: 'quiet', meaning: '安静的' },
            { word: 'strange', meaning: '奇怪的' },
            { word: 'zip', meaning: '拉上……拉链' },
          ],
          sentenceLearning: [
            {
              pattern: 'Are these/those your ...?',
              examples: ['Yes, they are./No, they aren\'t.', '-Are these your keys?', '-Yes, they are.', '-Are these your pens?', '-Yes, they are.', '-Are those your books?', '-No, they aren\'t.', '-Are those your bags?', '-No, they aren\'t.'],
            },
          ],
          phonicsLearning: '字母 Qq 发音：queen  quiet   quilt   quarter',
        },
      },
    ],
  },
  {
    id: 'G4-上-U8',
    grade: 'G4',
    semester: '上',
    unitNum: 8,
    title: 'Don\'t talk in the library',
    titleCn: '不要在图书馆交谈',
    theme: '规则与行为',
    vocab: [
      { word: 'talk', meaning: '交谈；说话' },
      { word: 'take', meaning: '带' },
      { word: 'write', meaning: '写；写作' },
      { word: 'eat', meaning: '吃' },
      { word: 'late', meaning: '迟的' },
      { word: 'class', meaning: '（一节）课；上课时间' },
      { word: 'wall', meaning: '墙；墙壁' },
      { word: 'litter', meaning: '乱扔垃圾' },
      { word: 'say', meaning: '说' },
      { word: 'word', meaning: '单词；词' },
      { word: 'down', meaning: '向下；往下' },
      { word: 'of', meaning: '……的' },
      { word: 'always', meaning: '总是' },
      { word: 'film', meaning: '电影' },
      { word: 'interesting', meaning: '有趣的' },
      { word: 'magic', meaning: '有魔力的；神奇的' },
      { word: 'reading room', meaning: '阅览室' },
      { word: 'really', meaning: '确实；的确' },
      { word: 'rule', meaning: '规则' },
      { word: 'sunshine', meaning: '阳光' },
    ],
    sentences: [
      {
        pattern: 'Don’t talk in the library.',
        examples: [],
      },
      {
        pattern: 'Don’t write on the book.',
        examples: [],
      },
      {
        pattern: 'Don’t eat in the library.',
        examples: [],
      },
      {
        pattern: 'Don’t eat in class.',
        examples: [],
      },
      {
        pattern: 'Don’t be late for school.',
        examples: [],
      },
      {
        pattern: 'Don’t litter.',
        examples: [],
      },
    ],
    text: {
      partA: 'Liling takes Dongdong to the library.\nThey are in the reading room.\n\nA: Ha-ha, it’s interesting.\nB: Shh! Don’t talk here.\nA: Don’t write on the book.\nB: I’m sorry.\nA: Don’t eat in the library.\nB: OK.',
    },
    phonics: {
      letter: 'Rr',
      words: ['red', 'robotrunrope'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'talk', meaning: '交谈；说话' },
            { word: 'take', meaning: '带' },
            { word: 'write', meaning: '写；写作' },
            { word: 'eat', meaning: '吃' },
            { word: 'late', meaning: '迟的' },
          ],
          sentenceLearning: [
            {
              pattern: 'Don\'t ...',
              examples: ['Don’t eat in the library.', 'Don’t eat in class.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'wall', meaning: '墙；墙壁' },
            { word: 'litter', meaning: '乱扔垃圾' },
            { word: 'say', meaning: '说' },
            { word: 'word', meaning: '单词；词' },
            { word: 'down', meaning: '向下；往下' },
          ],
          sentenceLearning: [
            {
              pattern: 'Don\'t ...',
              examples: ['Don’t talk in the library.', 'Don’t write on the book.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'always', meaning: '总是' },
            { word: 'film', meaning: '电影' },
            { word: 'interesting', meaning: '有趣的' },
            { word: 'magic', meaning: '有魔力的；神奇的' },
            { word: 'reading room', meaning: '阅览室' },
            { word: 'really', meaning: '确实；的确' },
            { word: 'rule', meaning: '规则' },
            { word: 'sunshine', meaning: '阳光' },
          ],
          sentenceLearning: [
            {
              pattern: 'Don\'t ...',
              examples: ['Don’t be late for school.', 'Don’t litter.'],
            },
          ],
          phonicsLearning: '字母 Rr 发音：red robot	run	rope',
        },
      },
    ],
  },
  {
    id: 'G4-上-U9',
    grade: 'G4',
    semester: '上',
    unitNum: 9,
    title: 'This is my home',
    titleCn: '这是我的家',
    theme: '房间与家居',
    vocab: [
      { word: 'home', meaning: '家；住所；窝' },
      { word: 'living room', meaning: '客厅' },
      { word: 'kitchen', meaning: '厨房' },
      { word: 'bedroom', meaning: '卧室' },
      { word: 'tidy', meaning: '整洁的' },
      { word: 'bathroom', meaning: '浴室；卫生间' },
      { word: 'chair', meaning: '椅子' },
      { word: 'desk', meaning: '书桌；办公桌' },
      { word: 'table', meaning: '桌子' },
      { word: 'cousin', meaning: '堂兄弟（姐妹）；表兄弟（姐妹）' },
      { word: 'house', meaning: '房子' },
      { word: 'parent', meaning: '父；母' },
      { word: 'TV', meaning: '电视' },
    ],
    sentences: [
      {
        pattern: 'This is the living room/kitchen/bathroom/bedroom.',
        examples: [],
      },
      {
        pattern: 'Is this the kitchen/ bathroom/bedroom/living room?',
        examples: [],
      },
      {
        pattern: '— Yes, it is. / No, it isn’t.',
        examples: [],
      },
      {
        pattern: 'There is a table/bed.',
        examples: [],
      },
      {
        pattern: 'There are some desks/chairs.',
        examples: [],
      },
    ],
    text: {
      partA: 'A: Welcome to my home! Come in, please.\nB: Thank you.\nA: This is the living room. \nB: It’s big.\nA: This is the kitchen. \nC:It’s clean.\n    Is this your bedroom, Mingming?\nA: Yes, it is.\nD: It’s tidy.',
    },
    phonics: {
      letter: 'Ss',
      words: ['seasitsaysand'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'home', meaning: '家；住所；窝' },
            { word: 'living room', meaning: '客厅' },
            { word: 'kitchen', meaning: '厨房' },
            { word: 'bedroom', meaning: '卧室' },
          ],
          sentenceLearning: [
            {
              pattern: 'This is the ...',
              examples: ['This is the living room/kitchen/bathroom/bedroom.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'tidy', meaning: '整洁的' },
            { word: 'bathroom', meaning: '浴室；卫生间' },
            { word: 'chair', meaning: '椅子' },
            { word: 'desk', meaning: '书桌；办公桌' },
            { word: 'table', meaning: '桌子' },
          ],
          sentenceLearning: [
            {
              pattern: 'Is this the ...?',
              examples: ['Yes, it is. / No, it isn’t.', 'Is this the kitchen/ bathroom/bedroom/living room?', '— Yes, it is. / No, it isn’t.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'cousin', meaning: '堂兄弟（姐妹）；表兄弟（姐妹）' },
            { word: 'house', meaning: '房子' },
            { word: 'parent', meaning: '父；母' },
            { word: 'TV', meaning: '电视' },
          ],
          sentenceLearning: [
            {
              pattern: 'There is a...',
              examples: ['There are some ....', 'There is a table.', 'There are some chairs.', 'There is a bed.', 'There are some desks.'],
            },
          ],
          phonicsLearning: '字母 Ss 发音：sea	sit	say	sand',
        },
      },
    ],
  },
  {
    id: 'G4-上-U10',
    grade: 'G4',
    semester: '上',
    unitNum: 10,
    title: 'What\'s his job?',
    titleCn: '他的工作是什么？',
    theme: '职业与梦想',
    vocab: [
      { word: 'job', meaning: '工作' },
      { word: 'photo', meaning: '照片' },
      { word: 'policeman', meaning: '男警察' },
      { word: 'player', meaning: '运动员；球员' },
      { word: 'be', meaning: '成为；是' },
      { word: 'wonderful', meaning: '令人钦佩的；令人高兴的' },
      { word: 'scientist', meaning: '科学家' },
      { word: 'astronaut', meaning: '宇航员' },
      { word: 'writer', meaning: '作家；作者' },
      { word: 'fireman', meaning: '消防员' },
      { word: 'policewoman', meaning: '女警察' },
      { word: 'fire', meaning: '火' },
      { word: 'with', meaning: '和……一起；具有；用' },
      { word: 'keep', meaning: '保持' },
      { word: 'around', meaning: '在……周围' },
      { word: 'artist', meaning: '画家；艺术家' },
      { word: 'dangerous', meaning: '危险的' },
      { word: 'everything', meaning: '每样东西' },
      { word: 'on fire', meaning: '着火' },
      { word: 'village', meaning: '村庄' },
    ],
    sentences: [
      {
        pattern: '-What’s his job?',
        examples: [],
      },
      {
        pattern: '-He’s a policeman. / He’s a doctor. / He’s a fireman.',
        examples: [],
      },
      {
        pattern: '-What’s her job?',
        examples: [],
      },
      {
        pattern: '-She’s a basketball player. / She’s a scientist. / She’s a teacher.',
        examples: [],
      },
      {
        pattern: '-Is she your mother?',
        examples: [],
      },
      {
        pattern: '-Yes, she is.',
        examples: [],
      },
      {
        pattern: '-Is he your father?',
        examples: [],
      },
      {
        pattern: '- No, he isn’t. He’s my uncle.',
        examples: [],
      },
      {
        pattern: 'I want to be a basketball player.',
        examples: [],
      },
      {
        pattern: 'I want to be a football player.',
        examples: [],
      },
      {
        pattern: 'I want to be an astronaut.',
        examples: [],
      },
      {
        pattern: 'I want to be an artist.',
        examples: [],
      },
    ],
    text: {
      partA: 'A: This is my family photo.\nB: What\'s your father\'s job?\nA: He’s a policeman.\nB: Is she your mother?\nA: Yes, she is.\nB: What’s her job?\nA: She’s a basketball player. \nB: I like basketball. I want to be a basketball player too.\nC: Wonderful!',
    },
    phonics: {
      letter: 'Tt',
      words: ['two', 'toy', 'tiger', 'taxi'],
    },
    lessons: [
      {
        lessonNum: 1,
        title: '课文学习 + 核心词汇 + 句型',
        sections: {
          textLearning: 'Part A 课文动画 → 中英匹配',
          vocabLearning: [
            { word: 'job', meaning: '工作' },
            { word: 'photo', meaning: '照片' },
            { word: 'policeman', meaning: '男警察' },
            { word: 'player', meaning: '运动员；球员' },
            { word: 'be', meaning: '成为；是' },
            { word: 'wonderful', meaning: '令人钦佩的；令人高兴的' },
            { word: 'scientist', meaning: '科学家' },
          ],
          sentenceLearning: [
            {
              pattern: 'What\'s his/her job?',
              examples: ['He/She is a(an) ...', '-What’s his job?', '-He’s a policeman.', '-What’s his job?', '-He’s an astronaut.', '-What’s her job?', '-She’s a scientist.', '-What’s her job?', '-She’s a writer.'],
            },
          ],
        },
      },
      {
        lessonNum: 2,
        title: '课文跟读 + 词汇拓展 + 句型',
        sections: {
          textLearning: 'Part A 课文跟读 → 对话排序',
          vocabLearning: [
            { word: 'astronaut', meaning: '宇航员' },
            { word: 'writer', meaning: '作家；作者' },
            { word: 'fireman', meaning: '消防员' },
            { word: 'policewoman', meaning: '女警察' },
            { word: 'fire', meaning: '火' },
            { word: 'with', meaning: '和……一起；具有；用' },
            { word: 'keep', meaning: '保持' },
          ],
          sentenceLearning: [
            {
              pattern: 'Is she/he your ...?',
              examples: ['Yes, she/he is./ No, she/he isn\'t. She/He is my...', '-Is she your mother?', '-Yes, she is.', '-Is she your grandma?', '-Yes, she is.', '-Is he your father?', '- No, he isn’t. He’s my uncle.', '-Is he your brother?', '- No, he isn’t. He’s my cousin.'],
            },
          ],
        },
      },
      {
        lessonNum: 3,
        title: '课文背诵 + 词汇拓展 + 句型 + 自然拼读',
        sections: {
          textLearning: 'Part A 课文背诵 → Part G 动画跟读',
          vocabLearning: [
            { word: 'around', meaning: '在……周围' },
            { word: 'artist', meaning: '画家；艺术家' },
            { word: 'dangerous', meaning: '危险的' },
            { word: 'everything', meaning: '每样东西' },
            { word: 'on fire', meaning: '着火' },
            { word: 'village', meaning: '村庄' },
          ],
          sentenceLearning: [
            {
              pattern: 'I want to be a(an)...',
              examples: ['I want to be a basketball player.', 'I want to be a football player.', 'I want to be an astronaut.', 'I want to be an artist.'],
            },
          ],
          phonicsLearning: '字母 Tt 发音：two toy tiger taxi',
        },
      },
    ],
  },
]


// ========================
// 学习计划
// ========================

export const plans: PlanConfig[] = [
  {
    type: 'A',
    name: 'Plan A - 领航强化',
    description: 'A层 · 输出强化：口语与拼写占比高，绘本与单词游戏穿插',
    features: ['A层 8-10分', '输出强化', '口语+拼写为主', '绘本拓展'],
    weeks: [
      { week: 1, tasks: ['单词游戏', '口语', '拼写', '绘本阅读', '口语', '拼写', '绘本阅读'] },
      { week: 2, tasks: ['口语', '拼写', '绘本阅读', '单词游戏', '口语', '拼写', '绘本阅读'] },
    ],
  },
  {
    type: 'B',
    name: 'Plan B - 标准均衡',
    description: 'B层 · 均衡轮排：听说读写四类内容均衡循环',
    features: ['B层 4-7分', '均衡轮排', '四类并重', '灵活安排'],
    weeks: [
      { week: 1, tasks: ['单词游戏', '口语', '绘本阅读', '拼写', '单词游戏', '口语', '绘本阅读'] },
      { week: 2, tasks: ['拼写', '绘本阅读', '单词游戏', '口语', '绘本阅读', '拼写', '单词游戏'] },
      { week: 3, tasks: ['口语', '绘本阅读', '拼写', '单词游戏', '口语', '绘本阅读', '拼写'] },
    ],
  },
  {
    type: 'C',
    name: 'Plan C - 新芽筑基',
    description: 'C层 · 输入为主：单词游戏与绘本阅读占比高，口语拼写温和',
    features: ['C层 0-3分', '输入为主', '单词游戏+绘本', '循序渐进'],
    weeks: [
      { week: 1, tasks: ['单词游戏', '绘本阅读', '单词游戏', '口语', '绘本阅读', '单词游戏', '拼写'] },
      { week: 2, tasks: ['绘本阅读', '单词游戏', '绘本阅读', '拼写', '单词游戏', '绘本阅读', '口语'] },
    ],
  },
]

// 六年级完整学期示例 (20周)
export const semesterPlanG6: { week: number; tasks: string[] }[] = [
  { week: 1, tasks: ['首测+反馈', '单词游戏', '单元学习1-1', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 2, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元学习1-2', '绘本阅读'] },
  { week: 3, tasks: ['周学习反馈', '单词游戏', '单元学习2', '口语拓展', '直播课', '单元学习3', '绘本阅读'] },
  { week: 4, tasks: ['周学习反馈', '单词游戏', '单元学习1-3', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 5, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元学习1-4', '绘本阅读'] },
  { week: 6, tasks: ['周学习反馈', '单词游戏', '单元学习2', '口语拓展', '直播课', '单元学习3', '绘本阅读'] },
  { week: 7, tasks: ['周学习反馈', '单词游戏', '单元学习1-5', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 8, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元复习', '绘本阅读'] },
  { week: 9, tasks: ['周学习反馈', '单词游戏', '单元复习', '口语拓展', '直播课', '单元复习', '绘本阅读'] },
  { week: 10, tasks: ['周学习反馈', '错题巩固', '单元复习', '单元检测', '直播课', '单元复习', '绘本阅读'] },
  { week: 11, tasks: ['周学习反馈', '单词游戏', '单元学习1-6', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 12, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元学习1-7', '绘本阅读'] },
  { week: 13, tasks: ['周学习反馈', '单词游戏', '单元学习2', '口语拓展', '直播课', '单元学习3', '绘本阅读'] },
  { week: 14, tasks: ['周学习反馈', '单词游戏', '单元学习1-8', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 15, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元学习1-9', '绘本阅读'] },
  { week: 16, tasks: ['周学习反馈', '单词游戏', '单元学习2', '口语拓展', '直播课', '单元学习3', '绘本阅读'] },
  { week: 17, tasks: ['周学习反馈', '单词游戏', '单元学习1-10', '口语拓展', '直播课', '单元学习2', '绘本阅读'] },
  { week: 18, tasks: ['周学习反馈', '错题巩固', '单元学习3', '单元检测', '直播课', '单元复习', '绘本阅读'] },
  { week: 19, tasks: ['周学习反馈', '单词游戏', '单元学习2', '口语拓展', '直播课', '单元复习', '绘本阅读'] },
  { week: 20, tasks: ['周学习反馈', '单词游戏', '单元复习', '口语拓展', '直播课', '单元复习', '绘本阅读', '本期学习反馈'] },
]

// ========================
// 前测题目 - 能力测试
// ========================

export const pretestQuestions: PretestQuestion[] = [
  // 三年级
  {
    id: 'pt-g3-1',
    grade: 'G3',
    dimension: '语音敏感度',
    subDimension: '音义联结',
    questionType: '听音选图',
    content: '听音选图：音频 cat',
    options: ['猫', '狗', '鸟'],
    correctAnswer: 0,
  },
  {
    id: 'pt-g3-2',
    grade: 'G3',
    dimension: '语音敏感度',
    subDimension: '语音辨别',
    questionType: '听音选词',
    content: '听音选词：音频 bat',
    options: ['dad', 'bat', 'cat'],
    correctAnswer: 1,
  },
  {
    id: 'pt-g3-3',
    grade: 'G3',
    dimension: '词汇储备',
    subDimension: '主题词汇',
    questionType: '词类辨析',
    content: '选出下列单词释义不同类的一项',
    options: ['red', 'yellow', 'pen'],
    correctAnswer: 2,
  },
  {
    id: 'pt-g3-4',
    grade: 'G3',
    dimension: '词汇储备',
    subDimension: '数词词汇',
    questionType: '图文选择',
    content: '选出单词 two 对应的图片',
    options: ['三个苹果', '两只狗', '五只小鸟'],
    correctAnswer: 1,
  },
  {
    id: 'pt-g3-5',
    grade: 'G3',
    dimension: '句法/指令理解',
    subDimension: '课堂指令',
    questionType: '听音选图',
    content: '听音选图：音频 Stand up.',
    options: ['起立', '躺下', '坐下'],
    correctAnswer: 0,
  },
  {
    id: 'pt-g3-6',
    grade: 'G3',
    dimension: '句法/指令理解',
    subDimension: '情境反应',
    questionType: '听音选图',
    content: '听音选图：Good morning!',
    options: ['下午放学', '早上上学', '晚上睡觉'],
    correctAnswer: 1,
  },
  // 四年级
  {
    id: 'pt-g4-1',
    grade: 'G4',
    dimension: '拼读迁移',
    subDimension: '规则识别',
    questionType: '语音选择',
    content: '找出元音字母发音不同的选项',
    options: ['cat', 'make', 'bag'],
    correctAnswer: 1,
  },
  {
    id: 'pt-g4-2',
    grade: 'G4',
    dimension: '拼读迁移',
    subDimension: '见词能读',
    questionType: '跟读',
    content: '请拼读下列单词：ten',
    options: [],
    correctAnswer: 0,
  },
  {
    id: 'pt-g4-3',
    grade: 'G4',
    dimension: '拼读迁移',
    subDimension: '见词能读',
    questionType: '跟读',
    content: '请拼读下列单词：fit',
    options: [],
    correctAnswer: 0,
  },
  {
    id: 'pt-g4-4',
    grade: 'G4',
    dimension: '拼读迁移',
    subDimension: '见词能读',
    questionType: '跟读',
    content: '请拼读下列单词：cut',
    options: [],
    correctAnswer: 0,
  },
  {
    id: 'pt-g4-5',
    grade: 'G4',
    dimension: '词汇储备',
    subDimension: '主题词汇',
    questionType: '词类解析',
    content: '选出下列单词释义不同类的一项',
    options: ['pencil', 'book', 'sheep'],
    correctAnswer: 2,
  },
  {
    id: 'pt-g4-6',
    grade: 'G4',
    dimension: '词汇储备',
    subDimension: '词义反应',
    questionType: '听音选择图片',
    content: '听句子选图：There is a ruler on the desk.',
    options: ['床上放着娃娃', '小朋友看书', '课桌上放着尺'],
    correctAnswer: 2,
  },
  {
    id: 'pt-g4-7',
    grade: 'G4',
    dimension: '词汇储备',
    subDimension: '单词拼写',
    questionType: '根据提示，选字母补全',
    content: 'c____ful',
    options: ['aer', 'eer', 'are'],
    correctAnswer: 2,
  },
  {
    id: 'pt-g4-8',
    grade: 'G4',
    dimension: '词汇储备',
    subDimension: '单词拼写',
    questionType: '根据提示，选字母补全',
    content: 'pic____',
    options: ['ture', 'tour', 'oure'],
    correctAnswer: 0,
  },
  {
    id: 'pt-g4-9',
    grade: 'G4',
    dimension: '句法理解',
    subDimension: '句型反应',
    questionType: '听音选择答语',
    content: '听问句选择合适的答句：Who is she?',
    options: ['She is my aunt.', 'He is a doctor.', "It's a bird."],
    correctAnswer: 0,
  },
  {
    id: 'pt-g4-10',
    grade: 'G4',
    dimension: '句法理解',
    subDimension: '情境应答',
    questionType: '听音判断图片',
    content: '根据对话选择正确的图片：\nA: How do you go to school?\nB: I go to school on foot.',
    options: ['两人在打招呼', '两人在操场玩', '两人在交谈，其中一人气泡框显示“步行上学”'],
    correctAnswer: 2,
  },
  // 五年级
  {
    id: 'pt-g5-1',
    grade: 'G5',
    dimension: '语音规则',
    subDimension: '见词识别',
    questionType: '语音选择',
    content: '哪个单词与sheep的元音部分发音相同？',
    options: ['green', 'weather', 'show'],
    correctAnswer: 0,
  },
  {
    id: 'pt-g5-2',
    grade: 'G5',
    dimension: '句法与语篇',
    subDimension: '语篇理解',
    questionType: '阅读选择',
    content: 'Lily had a busy weekend. On Saturday morning, she did her homework. In the afternoon, she went hiking. On Sunday, she visited her grandparents.\nWhat did Lily do on Saturday afternoon?',
    options: ['She did her homework.', 'She went hiking.', 'She visited her grandparents.'],
    correctAnswer: 1,
  },
  // 六年级
  {
    id: 'pt-g6-1',
    grade: 'G6',
    dimension: '语音综合',
    subDimension: '见词辨音',
    questionType: '语音选择',
    content: '下列单词中与bird的元音发音相同的单词是？',
    options: ['her', 'see', 'sing'],
    correctAnswer: 0,
  },
  {
    id: 'pt-g6-2',
    grade: 'G6',
    dimension: '语篇理解',
    subDimension: '信息推理',
    questionType: '阅读选择',
    content: 'Tom: "I lost my dog yesterday." Mary: "Yes. I saw it near the park gate this morning."\nWhat did Mary do?',
    options: ['She lost a dog.', "She saw Tom's dog this morning.", 'She found the dog and took it home.'],
    correctAnswer: 1,
  },
]

// ========================
// 学习偏好测试
// ========================

export const learningStyleQuestions: LearningStyleQuestion[] = [
  {
    id: 'ls-1',
    question: '学习课文时，你更喜欢？',
    options: [
      { text: '把它放进一个有趣故事里', type: 'imagination' },
      { text: '用图片展示出来', type: 'visual' },
      { text: '马上跟着说一说', type: 'expression' },
      { text: '完成一个挑战任务', type: 'challenge' },
    ],
  },
  {
    id: 'ls-2',
    question: '遇到难懂内容时，你更希望：',
    options: [
      { text: '放到真实场景里解释', type: 'imagination' },
      { text: '画图告诉我', type: 'visual' },
      { text: '让我试着说一说', type: 'expression' },
      { text: '给我一个练习任务', type: 'challenge' },
    ],
  },
  {
    id: 'ls-3',
    question: '学完后怎样最容易记住？',
    options: [
      { text: '记住故事情节', type: 'imagination' },
      { text: '记住图片画面', type: 'visual' },
      { text: '自己说过一次', type: 'expression' },
      { text: '完成挑战成功', type: 'challenge' },
    ],
  },
  {
    id: 'ls-4',
    question: '你最期待哪种学习奖励？',
    options: [
      { text: '解锁新的冒险故事', type: 'imagination' },
      { text: '收集精美卡牌', type: 'visual' },
      { text: '录制自己的作品', type: 'expression' },
      { text: '获得新徽章', type: 'challenge' },
    ],
  },
]

export const learningStyleProfiles: LearningStyleProfile[] = [
  {
    type: 'imagination',
    name: '故事探索型',
    avatar: '宝藏侦探',
    description: '喜欢听故事、探险和解谜，通过剧情学习新知识。',
    emoji: '🕵️',
  },
  {
    type: 'challenge',
    name: '挑战竞技型',
    avatar: '先锋勇士',
    description: '喜欢通过闯关、比赛、排行榜激励自己学习。',
    emoji: '⚔️',
  },
  {
    type: 'visual',
    name: '游戏闯关型',
    avatar: '魔法学徒',
    description: '喜欢通过游戏、任务和道具来学习，边玩边学。',
    emoji: '🪄',
  },
  {
    type: 'expression',
    name: '任务完成型',
    avatar: '建造大师',
    description: '喜欢有明确目标和步骤，通过达成目标获得成就感。',
    emoji: '🔨',
  },
  {
    type: 'expression',
    name: '社交互动型',
    avatar: '有爱使者',
    description: '喜欢与同伴一起学习，通过交流、合作和分享获得学习动力。',
    emoji: '🤝',
  },
]

// ========================
// 分层策略
// ========================

export const layerStrategies = {
  A: {
    name: 'A层 - 领航探险家',
    scoreRange: '8-10分',
    description: '输入环节压缩，简化操练，输出难度提升',
    color: 'mint',
    vocabStrategy: '可跳过输入环节，直接进行拼写和运用挑战',
    sentenceStrategy: '自主造句，情景交际，灵活替换',
    textStrategy: '课文背诵+拓展运用',
    phonicsStrategy: 'CVC拼读拓展，尝试拼读新词',
  },
  B: {
    name: 'B层 - 追光探险家',
    scoreRange: '4-7分',
    description: '标准路径：完整输入→游戏操练→半控制输出',
    color: 'brand',
    vocabStrategy: '标准学习路径：单词卡→听音选图→跟读→拼写',
    sentenceStrategy: '句型视频→选图→连词成句',
    textStrategy: '课文跟读→排序→背诵',
    phonicsStrategy: '律动视频→听音选词→读单词',
  },
  C: {
    name: 'C层 - 新芽探险家',
    scoreRange: '0-3分',
    description: '强化输入，输出降级',
    color: 'warm',
    vocabStrategy: '强化输入：多看多听，辅助提示完成拼写',
    sentenceStrategy: '增加提示和引导，降低输出难度',
    textStrategy: '多轮跟读，图片辅助理解',
    phonicsStrategy: '增加跟读次数，重点掌握基础发音',
  },
}

// ========================
// 检测题型设计
// ========================

export const assessmentQuestionTypes: AssessmentQuestionType[] = [
  // 单词板块
  { id: 'q1', category: '单词', subCategory: '听音选图', frontendType: '单选题', backendType: '听音选择图片', description: '听单词音频，选择对应的图片' },
  { id: 'q2', category: '单词', subCategory: '看图说词', frontendType: '口语题', backendType: '看图说词', description: '看图片，说出对应的英文单词' },
  { id: 'q3', category: '单词', subCategory: '单词拼写', frontendType: '填空题', backendType: '选字母补全', description: '根据提示，选择字母补全单词' },
  // 句型板块
  { id: 'q4', category: '句型', subCategory: '听句子选图', frontendType: '单选题', backendType: '听音选择图片', description: '听句子，选择对应的图片' },
  { id: 'q5', category: '句型', subCategory: '中英互译', frontendType: '单选题', backendType: '句型翻译', description: '选择句子的正确翻译' },
  { id: 'q6', category: '句型', subCategory: '看图说话', frontendType: '口语题', backendType: '看图说句子', description: '看图片，说出对应的英文句子' },
  { id: 'q7', category: '句型', subCategory: '连词成句', frontendType: '排序题', backendType: '连词成句', description: '将打乱的单词排列成正确的句子' },
  // 课文板块
  { id: 'q8', category: '课文', subCategory: '读句子', frontendType: '口语题', backendType: 'ISE声学评分', description: '朗读句子，由发音评测服务分析完整度、准确度、流利度和标准度' },
  { id: 'q9', category: '课文', subCategory: '看中文说句子', frontendType: '口语题', backendType: '中译英口语', description: '看中文，说出英文句子' },
  { id: 'q10', category: '课文', subCategory: '课文排序', frontendType: '排序题', backendType: '课文排序', description: '将课文句子按正确顺序排列' },
  // 自拼板块
  { id: 'q11', category: '自拼', subCategory: '听音选词', frontendType: '单选题', backendType: '听音选择字母/词汇', description: '听发音，选择对应的字母或单词' },
  { id: 'q12', category: '自拼', subCategory: '读单词', frontendType: '口语题', backendType: '跟读', description: '跟读单词，练习发音' },
]

// ========================
// 自然拼读歌词
// ========================

export const phonicsLyrics: { letter: string; lyric: string; emoji: string; words: string[] }[] = [
  { letter: 'Ll', words: ['lion', 'light', 'line', 'letter'], lyric: 'L, L, Lion! Lion, Lion, look at the lion!\nL, L, Light! Light, light, turn on the light!\nL, L, Line! Draw a line, a long long line!\nL, L, Letter! Write a letter, a love letter!', emoji: '🦁' },
  { letter: 'Mm', words: ['monkey', 'moon', 'milk', 'mango'], lyric: 'M, M, Monkey! Monkey, monkey, jump up high!\nM, M, Moon! Moon, moon, in the sky!\nM, M, Milk! Drink your milk, it\'s good for you!\nM, M, Mango! Mango, mango, sweet and yellow!', emoji: '🐵' },
  { letter: 'Nn', words: ['nose', 'nest', 'night', 'nine'], lyric: 'N, N, Nose! Touch your nose, one two three!\nN, N, Nest! Birdie\'s nest, up in the tree!\nN, N, Night! Good night, sleep tight!\nN, N, Nine! Count to nine, one more time!', emoji: '👃' },
  { letter: 'Oo', words: ['orange', 'ocean', 'octopus', 'owl'], lyric: 'O, O, Orange! Orange, orange, juicy and round!\nO, O, Ocean! Ocean, ocean, hear the sound!\nO, O, Octopus! Eight arms, one two three four!\nO, O, Owl! Owl, owl, who-who-who!', emoji: '🍊' },
  { letter: 'Pp', words: ['pen', 'pig', 'pizza', 'panda'], lyric: 'P, P, Pen! Pen, pen, write with me!\nP, P, Pig! Pinky pig, oink oink oink!\nP, P, Pizza! Pizza, pizza, yummy cheese!\nP, P, Panda! Panda, panda, black and white!', emoji: '🐼' },
  { letter: 'Qq', words: ['queen', 'quilt', 'question', 'quiet'], lyric: 'Q, Q, Queen! Queen, queen, wears a crown!\nQ, Q, Quilt! Quilt, quilt, warm and brown!\nQ, Q, Question! What? What? Tell me now!\nQ, Q, Quiet! Shh... quiet please!', emoji: '👑' },
  { letter: 'Rr', words: ['rabbit', 'rain', 'rainbow', 'robot'], lyric: 'R, R, Rabbit! Rabbit, rabbit, hop hop hop!\nR, R, Rain! Rain, rain, go away!\nR, R, Rainbow! Red, orange, yellow, green!\nR, R, Robot! Robot, robot, beep boop beep!', emoji: '🐰' },
  { letter: 'Ss', words: ['sun', 'snake', 'star', 'sea'], lyric: 'S, S, Sun! Sun, sun, shining bright!\nS, S, Snake! Snake, snake, hiss hiss hiss!\nS, S, Star! Star, star, twinkle twinkle!\nS, S, Sea! Sea, sea, waves so blue!', emoji: '☀️' },
  { letter: 'Tt', words: ['tiger', 'tree', 'train', 'turtle'], lyric: 'T, T, Tiger! Tiger, tiger, orange and black!\nT, T, Tree! Tree, tree, tall and green!\nT, T, Train! Train, train, choo choo choo!\nT, T, Turtle! Turtle, turtle, slow and steady!', emoji: '🐯' },
  { letter: 'Uu', words: ['umbrella', 'up', 'uncle', 'unicorn'], lyric: 'U, U, Umbrella! Umbrella, umbrella, open wide!\nU, U, Up! Up, up, up, reach the sky!\nU, U, Uncle! Uncle, uncle, how are you?\nU, U, Unicorn! Unicorn, unicorn, magical and bright!', emoji: '☂️' },
]

// ========================
// 教材版本对比
// ========================

export const textbookComparison = {
  headers: ['单元', '湘鲁版', '新人教版', '湘少版', '主题共性'],
  rows: [
    { unit: 'U1', xianglu: 'Hello! (问候与姓名)', renjiao: 'Making friends - How do we greet friends?', xiangshao: 'Hello! (问候)', common: '完全重合：问候交友' },
    { unit: 'U2', xianglu: 'Nice to meet you! (介绍朋友)', renjiao: 'Different families - Who lives with you?', xiangshao: 'What\'s your name? (姓名)', common: '部分重合：自我介绍' },
    { unit: 'U3', xianglu: 'How are you today? (日常问候)', renjiao: 'Amazing animals - What pets do you know?', xiangshao: 'How old are you? (年龄)', common: '独立主题' },
    { unit: 'U4', xianglu: 'She is my grandmother (家庭成员)', renjiao: 'Plants around us - What do we get from plants?', xiangshao: 'This is my family (家庭)', common: '完全重合：家庭成员' },
    { unit: 'U5', xianglu: 'He is a cook (职业)', renjiao: 'The colourful world - What colours do you see?', xiangshao: 'Is this your pen? (学习用品)', common: '独立主题' },
    { unit: 'U6', xianglu: 'His hair is short (外貌描述)', renjiao: 'Useful numbers - When do we use numbers?', xiangshao: 'Touch your head (身体部位)', common: '独立主题' },
    { unit: 'U7', xianglu: 'Do you like birds? (宠物喜好)', renjiao: 'Revision', xiangshao: 'What colour is it? (颜色)', common: '部分重合：颜色/动物' },
    { unit: 'U8', xianglu: 'How many dogs are there? (数量)', renjiao: '-', xiangshao: 'What\'s this? (动物)', common: '完全重合：动物话题' },
    { unit: 'U9', xianglu: 'I have a white rabbit (宠物颜色)', renjiao: '-', xiangshao: 'I like apples (水果)', common: '独立主题' },
    { unit: 'U10', xianglu: '-', renjiao: '-', xiangshao: 'Happy birthday! (生日)', common: '独立主题' },
  ],
}

// ========================
// 个性化资源推送对照表
// ========================

export const resourceRecommendations: { question: string; options: { text: string; type: string; resources: string[] }[] }[] = [
  {
    question: '学新东西时，你更喜欢？',
    options: [
      { text: 'A 安静看书/看图（视觉型）', type: 'visual', resources: ['单词图解闪卡', '绘本式单词书', '漫画语法故事'] },
      { text: 'B 听讲（听觉型）', type: 'auditory', resources: ['单词歌谣', '音频单词课', '有声绘本', '讲解版记忆法视频'] },
      { text: 'C 动手做（动觉型）', type: 'kinesthetic', resources: ['单词拼写积木', '可打印手工', 'TPR动作教学视频'] },
    ],
  },
  {
    question: '周末下午，你最容易自己选择做什么？',
    options: [
      { text: 'A 看绘本/漫画/听故事', type: 'visual', resources: ['分级绘本', '单词漫画', '听故事学单词播客', '故事版单词卡'] },
      { text: 'B 积木/手工/乐高', type: 'kinesthetic', resources: ['单词拼贴板', '乐高字母块'] },
      { text: 'C 电脑/平板游戏/视频', type: 'digital', resources: ['单词闯关游戏', '互动动画课', '单词消消乐', '趣味单词视频'] },
      { text: 'D 跑跳/打球/跳绳', type: 'kinesthetic', resources: ['运动学单词视频', '动作指令游戏'] },
    ],
  },
]
